import React from "react";
import { useUsageLimits } from "#next/hooks/use-usage-limits";
import Link from "next/link";
import { title } from "#/utils/general";

// Component to display usage limits
export const UsageLimitsDisplay: React.FC = () => {
  const { data: usageLimits, isLoading, error } = useUsageLimits();

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-indigo-50/90 to-violet-50/90 p-5 rounded-xl border border-indigo-100 shadow-md backdrop-blur-sm">
        <div className="flex items-center justify-center space-x-3">
          <div className="h-5 w-5 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin"></div>
          <p className="text-indigo-700 font-medium">Loading usage information...</p>
        </div>
      </div>
    );
  }

  if (error || !usageLimits) {
    return (
      <div className="bg-gradient-to-r from-red-50/90 to-orange-50/90 p-5 rounded-xl border border-red-100 shadow-md backdrop-blur-sm">
        <div className="flex items-start gap-4">
          <div className="mt-0.5 text-red-600 bg-red-100 rounded-full p-2 flex-shrink-0 shadow">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line
                x1="12"
                y1="8"
                x2="12"
                y2="12"></line>
              <line
                x1="12"
                y1="16"
                x2="12.01"
                y2="16"></line>
            </svg>
          </div>
          <div>
            <p className="text-red-700 font-medium leading-relaxed">
              Unable to load usage information.
            </p>
            <p className="text-red-500 text-sm mt-1">Please try refreshing the page.</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate the percentage used for the progress bar
  const percentageUsed = Math.min(100, Math.round((usageLimits.commandsUsed / (usageLimits.commandsUsed + usageLimits.recurringCommandsRemaining)) * 100));

  return (
    <div className="bg-gradient-to-r from-indigo-50/90 to-violet-50/90 p-5 rounded-xl border border-indigo-100 shadow-md backdrop-blur-sm">
      <div className="flex items-start gap-4">
        <div className="mt-0.5 text-indigo-600 bg-indigo-100 rounded-full p-2 flex-shrink-0 shadow transform transition-transform hover:rotate-12 hover:scale-110 duration-300">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M12 20v-6M6 20V10M18 20V4"></path>
          </svg>
        </div>
        <div className="w-full">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold text-indigo-700">Usage Limits</h4>
            <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
              {`${title(usageLimits.tier)} Plan`}
            </span>
          </div>

          <div className="mt-3">
            <div className="flex justify-between text-sm font-medium text-indigo-700 mb-1">
              <span>Commands Used: {usageLimits.commandsUsed}</span>
              <span>Remaining: {usageLimits.recurringCommandsRemaining}</span>
            </div>
            <div className="w-full bg-indigo-100 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${usageLimits.hasReachedRecurringLimit ? "bg-red-500" : "bg-indigo-500"}`}
                style={{ width: `${percentageUsed}%` }}
              ></div>
            </div>
            {(usageLimits.hasReachedRecurringLimit && !usageLimits.hasUnitPricing) && (
              <p className="text-red-600 text-sm font-medium mt-2 flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line
                    x1="12"
                    y1="9"
                    x2="12"
                    y2="13"></line>
                  <line
                    x1="12"
                    y1="17"
                    x2="12.01"
                    y2="17"></line>
                </svg>
                You've reached your usage limit. {usageLimits.isFreeTier && <Link prefetch href="/pricing" className="underline hover:text-red-700">Upgrade to Pro</Link>}
              </p>
            )}
            {!usageLimits.hasReachedRecurringLimit && usageLimits.isFreeTier && (
              <p className="text-indigo-600 text-sm mt-2">
                Upgrade to Pro for higher limits. <Link prefetch href="/pricing" className="underline hover:text-indigo-700">View plans</Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};